import { Card as AntCard } from 'antd';
import Link from 'antd/es/typography/Link';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize, IconVariant } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';
import { Tooltip } from '../Tooltip';

export interface PaymentCardProps {
  payorLabel: string;
  paymentInitiatorLabel?: string;
  organizationName: string;
  organizationInfoText?: string;
  onViewOrganization?: () => void;
  onCopyOrganizationName?: () => void;
  viewOrganizationText?: string;
  partner?: boolean;
  paymentMethodLabel: string;
  paymentMethodIcon?: IconType;
  paymentMethodVerified?: boolean;
  fsInfo?: boolean;
  fsInfoLabel?: string;
  expedited?: boolean;
  expeditedLabel?: string;
  className?: string;
  'data-testid'?: string;
}

const StyledCard = styled(AntCard)`
  &.ant-card {
    display: inline-block;
    width: fit-content;
    border-radius: ${theme.dime.radius.m};
    background-color: ${theme.dime.colors.surface.secondary};
    border: 1px solid ${theme.dime.colors.border.static};
    box-shadow: none;

    .ant-card-body {
      display: flex;
      flex-direction: column;
      padding: ${theme.dime.spacing.xl} ${theme.dime.spacing['2xl']};
      gap: ${theme.dime.spacing.m};

      /* antd's float clearfix pseudo-elements become flex items once
         display: flex is set above, so they pick up the gap and add
         phantom space before/after the real content. */
      &::before,
      &::after {
        display: none;
      }
    }
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.dime.spacing.s};
  width: 100%;
`;

const PayorGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: ${theme.dime.radius.s};
  background: transparent;
  cursor: pointer;
`;

// The info icon is a code-only addition (not in Figma) — reveal it on hover
// so it doesn't compete visually with the always-visible copy icon Figma
// specifies for the organization name.
const HoverRevealIconButton = styled(IconButton)`
  opacity: 0;
  transition: opacity 0.15s;
`;

const OrgNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};

  &:hover ${HoverRevealIconButton},
  &:focus-within ${HoverRevealIconButton} {
    opacity: 1;
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex !important;
  align-items: center;
  gap: ${theme.dime.spacing.s};
  font-size: ${theme.dime.fonts.sizes.xs};
  font-family: ${theme.dime.fonts.families.roboto};
  color: ${theme.dime.colors.link.primary} !important;

  &&:hover {
    color: ${theme.dime.colors.link.primaryHover} !important;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: ${theme.dime.spacing.l};
  width: 100%;
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.xl};
`;

const DetailGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.m};
`;

const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};
`;

const COPIED_FEEDBACK_MS = 1500;

type CopyStatus = 'idle' | 'copied' | 'failed';

const COPY_TOOLTIP_TEXT: Record<CopyStatus, string> = {
  idle: 'Copy organization name',
  copied: 'Copied!',
  failed: "Couldn't copy"
};

const COPY_ICON_TYPE: Record<CopyStatus, IconType> = {
  idle: IconType.Copy,
  copied: IconType.Approve,
  failed: IconType.Warning
};

const COPY_ICON_VARIANT: Record<CopyStatus, IconVariant> = {
  idle: IconVariant.Outline,
  copied: IconVariant.Outline,
  failed: IconVariant.Solid
};

const COPY_ICON_COLOR: Record<CopyStatus, string> = {
  idle: theme.dime.colors.icon.link,
  copied: theme.dime.colors.icon.success,
  failed: theme.dime.colors.icon.critical
};

export const PaymentCard: React.FC<PaymentCardProps> = ({
  payorLabel,
  paymentInitiatorLabel,
  organizationName,
  organizationInfoText,
  onViewOrganization,
  onCopyOrganizationName,
  viewOrganizationText = 'View organization',
  partner = true,
  paymentMethodLabel,
  paymentMethodIcon = IconType.Card,
  paymentMethodVerified = true,
  fsInfo = true,
  fsInfoLabel = 'FS info',
  expedited = true,
  expeditedLabel = 'Fast ACH',
  className,
  'data-testid': dataTestId
}) => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const copyResetTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      window.clearTimeout(copyResetTimeoutRef.current);
    };
  }, []);

  const copyOrganizationNameToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(organizationName);
      onCopyOrganizationName?.();
      return true;
    } catch {
      return false;
    }
  };

  // Shows the icon-swap feedback (checkmark / warning) — used by the
  // dedicated copy icon button only.
  const handleCopyIconClick = async () => {
    const success = await copyOrganizationNameToClipboard();
    setCopyStatus(success ? 'copied' : 'failed');
    window.clearTimeout(copyResetTimeoutRef.current);
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setCopyStatus('idle');
    }, COPIED_FEEDBACK_MS);
  };

  // Clicking the organization name itself also copies, but silently —
  // no icon-swap indication, since the icon is visually detached from the text.
  const handleOrganizationNameClick = () => {
    void copyOrganizationNameToClipboard();
  };

  return (
    <StyledCard
      bordered={false}
      className={className}
      data-testid={dataTestId ?? 'payment-card'}
    >
      <HeaderRow>
        <PayorGroup>
          <Typography
            variant="body2Bold"
            color={theme.dime.colors.text.secondary}
          >
            {payorLabel}
          </Typography>
          {paymentInitiatorLabel ? (
            <Typography
              variant="body2Bold"
              color={theme.dime.colors.text.secondary}
            >
              {paymentInitiatorLabel}
            </Typography>
          ) : null}
        </PayorGroup>
        {onViewOrganization ? (
          <StyledLink onClick={onViewOrganization}>
            {viewOrganizationText}
            <IconWrap
              type={IconType.ExternalLink}
              size={IconSize.Small}
              variant={IconVariant.Outline}
            />
          </StyledLink>
        ) : null}
      </HeaderRow>
      <DetailsContainer>
        <OrgNameRow>
          <Typography
            variant="heading3Bold"
            color={theme.dime.colors.link.primary}
            onClick={handleOrganizationNameClick}
          >
            {organizationName}
          </Typography>
          <IconButton
            type="button"
            aria-label={COPY_TOOLTIP_TEXT[copyStatus]}
            onClick={handleCopyIconClick}
          >
            <IconWrap
              type={COPY_ICON_TYPE[copyStatus]}
              size={IconSize.Small}
              variant={COPY_ICON_VARIANT[copyStatus]}
              style={{ color: COPY_ICON_COLOR[copyStatus] }}
            />
          </IconButton>
          {organizationInfoText ? (
            <Tooltip title={organizationInfoText}>
              <HoverRevealIconButton type="button" aria-label="Organization info">
                <IconWrap
                  type={IconType.Information}
                  size={IconSize.Small}
                  variant={IconVariant.Outline}
                  style={{ color: theme.dime.colors.icon.secondary }}
                />
              </HoverRevealIconButton>
            </Tooltip>
          ) : null}
        </OrgNameRow>
        <DetailsRow>
          {partner ? (
            <DetailGroup>
              <IconWrap
                type={IconType.MelioMe}
                size={IconSize.Medium}
                variant={IconVariant.Outline}
              />
              <Typography
                variant="heading4"
                color={theme.dime.colors.text.primary}
              >
                Melio
              </Typography>
            </DetailGroup>
          ) : null}
          <DetailGroup>
            <IconWrap
              type={paymentMethodIcon}
              size={IconSize.Medium}
              variant={IconVariant.Outline}
              style={{ color: theme.dime.colors.icon.primary }}
            />
            <LabelGroup>
              <Typography
                variant="heading4"
                color={theme.dime.colors.text.primary}
              >
                {paymentMethodLabel}
              </Typography>
              {paymentMethodVerified ? (
                <IconWrap
                  type={IconType.Approved}
                  size={IconSize.Small}
                  variant={IconVariant.Solid}
                  style={{ color: theme.dime.colors.icon.success }}
                />
              ) : null}
            </LabelGroup>
          </DetailGroup>
          {fsInfo ? (
            <LabelGroup>
              <Typography
                variant="heading4"
                color={theme.dime.colors.text.primary}
              >
                {fsInfoLabel}
              </Typography>
              <IconWrap
                type={IconType.Information}
                size={IconSize.Small}
                variant={IconVariant.Outline}
                style={{ color: theme.dime.colors.icon.secondary }}
              />
            </LabelGroup>
          ) : null}
          {expedited ? (
            <DetailGroup>
              <IconWrap
                type={IconType.PaymentExpedited}
                size={IconSize.Medium}
                variant={IconVariant.Outline}
                style={{ color: theme.dime.colors.icon.primary }}
              />
              <Typography
                variant="heading4"
                color={theme.dime.colors.text.primary}
              >
                {expeditedLabel}
              </Typography>
            </DetailGroup>
          ) : null}
        </DetailsRow>
      </DetailsContainer>
    </StyledCard>
  );
};
